import _ from 'lodash';
import { plural, t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { useState, useEffect } from 'react';
import { Tabs, Tab, Button, Box, Menu, MenuItem } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AdminPage from '../AdminPage';
import { AdminPages, buildPageId } from '../AdminLinkUtils';
import Actions from '../../page-framework/actions/Actions';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { usePageStateValueWithDefault, usePageState } from '../../../stores/PageStateStore';
import { getUrlParamFromState } from '../../../utilities/PageStateUrlUtilities';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import { GENERIC_SERVER_ERROR } from '../../../services/FormErrorService';
import { useSuccessfulAction } from '../../../utilities/FormUtilities';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';

import CertificateListPanel from './CertificateListPanel';
import CertificateCSRDialog from './CertificateCSRDialog';
import CertificateImportDialog from './CertificateImportDialog';
import { useCertificateTable } from './useCertificateTable';

import Link from '../../links/Link';

import {
    CERTIFICATE_USAGES,
    CERTIFICATE_DATA_SOURCE,
    TRUSTED_DATA_SOURCE,
    TAB_CERTIFICATES,
    TAB_TRUSTED,
    CERTIFICATE_NAME_FIELD,
    CERTIFICATE_IN_USE_ERRORS,
} from './CertificateConstants';

function collectDeleteErrors(deleteErrors) {
    const errors = [];

    errors.push(
        i18n._(
            plural(deleteErrors.length, {
                one: `${deleteErrors.length} certificate was not deleted because of the following error:`,
                other: `${deleteErrors.length} certificates were not deleted because of the following error(s):`,
            })
        )
    );

    const errTypes = new Set(_.map(deleteErrors, (error) => error.userMessageKey));
    const wifiErrors = _.filter(deleteErrors, (e) => e.userMessageKey === CERTIFICATE_IN_USE_ERRORS.WIFI);
    const wiredErrors = _.filter(deleteErrors, (e) => e.userMessageKey === CERTIFICATE_IN_USE_ERRORS.WIRED);
    const webErrors = _.filter(deleteErrors, (e) => e.userMessageKey === CERTIFICATE_IN_USE_ERRORS.WEB);

    if (errTypes.size >= 2) {
        const example = [wifiErrors, wiredErrors, webErrors]
            .map((arr) => (arr.length ? _.first(arr).userMessage : ''))
            .filter((s) => s !== '')
            .join(', ');
        errors.push(i18n._(t`Certificates could not be deleted because they are in use. for example: ${example}`));
    } else if (wifiErrors.length > 2) {
        const example = _.first(wifiErrors).userMessage;
        errors.push(
            i18n._(
                t`Certificates could not be deleted because they are part of a Wi-Fi network. for example: ${example}`
            )
        );
    } else if (wiredErrors.length > 2) {
        const example = _.first(wiredErrors).userMessage;
        errors.push(
            i18n._(
                t`Certificates could not be deleted because they are part of a Wired network. for example: ${example}`
            )
        );
    } else if (webErrors.length > 2) {
        const example = _.first(webErrors).userMessage;
        errors.push(
            i18n._(t`Certificates could not be deleted because they are part of a Web test. for example: ${example}`)
        );
    } else {
        let genericError = false;
        _.each(deleteErrors, (error) => {
            if (
                error &&
                (error.userMessageKey === CERTIFICATE_IN_USE_ERRORS.WIFI ||
                    error.userMessageKey === CERTIFICATE_IN_USE_ERRORS.WIRED ||
                    error.userMessageKey === CERTIFICATE_IN_USE_ERRORS.WEB)
            ) {
                errors.push(error.userMessage);
            } else if (!genericError) {
                genericError = true;
                errors.push(i18n._(GENERIC_SERVER_ERROR));
            }
        });
    }

    return errors;
}

const functionName = AdminPages.AgentCertificates;

const certUsageSupportCSR = [CERTIFICATE_USAGES.wifi, CERTIFICATE_USAGES.wired, CERTIFICATE_USAGES.voip];

export default function CertificateAdminPage() {
    const formDeleteService = useFormDeleteService();
    const { deleteWrapper } = useFormDeleteWrapper();
    const certificatesTable = useCertificateTable();
    const trustedCertificatesTable = useCertificateTable();
    const [csrDialogOpen, setCsrDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [certUsage, setCertUsage] = useState(CERTIFICATE_USAGES.voip);
    const handleSuccessfulDelete = useSuccessfulAction();
    const [, uiAlertActions] = useUiAlerts();
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const [, pageStateActions] = usePageState();
    useEffect(() => {
        pageStateActions.initialize('certificatePage');
    }, [pageStateActions]);

    const [activeTab, setActiveTab] = usePageStateValueWithDefault('activeTab', TAB_CERTIFICATES);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleCSROpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCSRClose = () => {
        setAnchorEl(null);
    };

    const postDeleteHandler = (serverResponse) => {
        const { response } = serverResponse;
        const deleteErrors = _.chain(response)
            .filter({ deleted: false })
            .map((r) => _.get(r, 'error'))
            .value();

        if (_.isEmpty(deleteErrors)) {
            return handleSuccessfulDelete(response, null, refresher);
        }

        uiAlertActions.createAlert(createAlertConfig(ERROR, collectDeleteErrors(deleteErrors).join('  ')));
        refresher();
    };

    const tabActionConfig = {
        [TAB_CERTIFICATES]: {
            table: certificatesTable,
            showCSR: true,
            dataSource: CERTIFICATE_DATA_SOURCE,
        },
        [TAB_TRUSTED]: {
            table: trustedCertificatesTable,
            showCSR: false,
            dataSource: TRUSTED_DATA_SOURCE,
        },
    };

    const { table, showCSR, dataSource } = tabActionConfig[activeTab];

    const handleConfirmedDelete = () => {
        const { tableState, selection } = table;
        const params = _.merge(
            {},
            {
                search: tableState?.search || '',
                sort: CERTIFICATE_NAME_FIELD,
                rowLimit: 2000,
            },
            dataSource.params
        );

        const deleteUrl = `${dataSource.crudUrl}/selection/softDelete`;

        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteSelection(deleteUrl, params, selection),
            refresherFunc: refresher,
            onDeleteGoTo: `/admin/certificate?${getUrlParamFromState({ activeTab })}`,
            postDeleteHandler,
        })();
    };
    const handleDownloadClick = (usage) => {
        setCertUsage(usage);
        setAnchorEl(null);
        setCsrDialogOpen(true);
    };
    const handleImportClick = () => {
        setImportDialogOpen(true);
    };

    return (
        <AdminPage
            title={t`Certificates`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <ConfirmDeleteButton
                        disabled={table.actionsDisabled}
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={table?.selection?.selection?.length > 1}
                    />
                    {showCSR && (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleCSROpen}
                                endIcon={<ArrowDropDownIcon />}
                            >
                                GENERATE CSR
                            </Button>

                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCSRClose}
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                PaperProps={{
                                    style: {
                                        minWidth: '160px',
                                    },
                                }}
                            >
                                {certUsageSupportCSR.map((usage) => (
                                    <MenuItem key={usage} onClick={() => handleDownloadClick(usage)}>
                                        {usage}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Button variant="contained" onClick={handleImportClick} color="primary">
                                <Trans>DEPLOY</Trans>
                            </Button>
                        </>
                    )}
                    <Button
                        component={Link}
                        to={`/admin/certificate/new?${getUrlParamFromState({ activeTab })}`}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>IMPORT</Trans>
                    </Button>
                </Actions>
            }
        >
            <>
                {csrDialogOpen && (
                    <CertificateCSRDialog open={csrDialogOpen} setOpen={setCsrDialogOpen} certUsage={certUsage} />
                )}
                {importDialogOpen && <CertificateImportDialog open={importDialogOpen} setOpen={setImportDialogOpen} />}
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label={t`Certificates`} value={TAB_CERTIFICATES} />
                    <Tab label={t`Trusted Certificates`} value={TAB_TRUSTED} />
                </Tabs>
                <Box p={2} style={{ display: 'flex', height: '100%' }}>
                    <CertificateListPanel
                        tableState={table}
                        dataSource={dataSource}
                        activeTab={activeTab}
                        triggerRef={triggerRef}
                    />
                </Box>
            </>
        </AdminPage>
    );
}
